import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Users, CheckCircle2, MessageSquare, TrendingUp, FileText, Settings, LogOut, Calendar, Percent, UserPlus, Lock, Lightbulb, AlertTriangle, Shield, MapPin, Target, MessageCircle, Award, ArrowUpRight, ArrowDownRight, Search, Filter, Star, Zap, Clock, Eye, X } from 'lucide-react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { getStats, getDomainSatisfaction, getPriorities, getComments } from '../api';
import toast from 'react-hot-toast';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [domains, setDomains] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentSearch, setCommentSearch] = useState('');
  const [period, setPeriod] = useState('all');
  const [source, setSource] = useState('all');
  const [quartierFilter, setQuartierFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedDomain, setSelectedDomain] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    if (user.role === 'agent') { navigate('/agent'); return; }
    loadStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const loadStats = async (p = period, s = source, q = quartierFilter) => {
    setLoading(true);
    try {
      const filters = {};
      if (p !== 'all') filters.period = p;
      if (s !== 'all') filters.source = s;
      if (q !== 'all') filters.quartier = q;
      // Si on filtre par source=agent et par quartier, le quartier doit venir d'une session agent
      const statsRes = await getStats(filters);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.clear(); navigate('/admin'); return; }
      toast.error('Erreur stats');
    }
    try { setDomains((await getDomainSatisfaction()).data.domains || []); } catch (e) { console.log('Domaines non dispo', e.message); }
    try { setPriorities((await getPriorities()).data.priorities || []); } catch (e) { console.log('Priorités non dispo', e.message); }
    try { setComments((await getComments()).data.comments || []); } catch (e) { console.log('Commentaires non dispo', e.message); }
    setLoading(false);
  };

  const handleLogout = () => { localStorage.clear(); navigate('/admin'); };

  if (loading) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="animate-pulse">Chargement...</div></div>;
  }

  const chartColors = ['#2E7D32', '#4CAF50', '#81C784', '#FFD54F', '#FFA000', '#E53935'];

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7f5' }}>
      {/* Sidebar */}
      <div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: '240px', background: 'linear-gradient(180deg, #1B5E20, #2E7D32)', padding: '24px 16px', display: 'flex', flexDirection: 'column', zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '40px', padding: '0 8px' }}>
          <img src="/logo.webp" alt="" style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'white', padding: '2px' }} />
          <div>
            <div style={{ color: '#FFD54F', fontSize: '10px', fontWeight: '600', letterSpacing: '0.5px' }}>ADMIN</div>
            <div style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>Khombole</div>
          </div>
        </div>

        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {[
            { icon: <BarChart3 size={18} />, label: 'Tableau de bord', path: '/admin/dashboard', active: true },
            { icon: <FileText size={18} />, label: 'Questions', path: '/admin/questions' },
            { icon: <MessageSquare size={18} />, label: 'Reponses', path: '/admin/responses' },
            { icon: <UserPlus size={18} />, label: 'Agents', path: '/admin/agents' },
            { icon: <Settings size={18} />, label: 'Paramètres', path: '/admin/settings' },
            { icon: <Lock size={18} />, label: 'Mon Compte', path: '/admin/profile' },
          ].map((item, i) => (
            <button key={i} onClick={() => navigate(item.path)}
              style={{ background: item.active ? 'rgba(255,255,255,0.15)' : 'transparent', border: 'none', color: 'white', padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: item.active ? '600' : '400', textAlign: 'left', width: '100%' }}>
              {item.icon} {item.label}
            </button>
          ))}
        </nav>

        <button onClick={handleLogout} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: 'rgba(255,255,255,0.7)', padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
          <LogOut size={18} /> Deconnexion
        </button>
      </div>

      {/* Main Content */}
      <div style={{ marginLeft: '240px', padding: '32px' }}>
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a1a', fontFamily: 'Playfair Display, serif' }}>Tableau de Bord</h1>
            <p style={{ color: '#888', fontSize: '13px', marginTop: '4px' }}>Vue d'ensemble de l'enquete citoyenne</p>
          </div>
        </div>

        {/* Filtres rapides */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#666', fontSize: '13px', fontWeight: '600' }}>
            <Filter size={14} /> Filtrer :
          </div>
          {[
            { key: 'all', label: 'Tout' },
            { key: 'today', label: "Aujourd'hui" },
            { key: 'week', label: 'Cette semaine' },
            { key: 'month', label: 'Ce mois' }
          ].map(f => {
            const active = period === f.key;
            return (
              <button key={f.key} onClick={() => { setPeriod(f.key); loadStats(f.key, source, quartierFilter); }}
                style={{ background: active ? '#1B5E20' : 'white', border: '1.5px solid ' + (active ? '#1B5E20' : '#e0e0e0'), color: active ? 'white' : '#555', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}>
                {f.label}
              </button>
            );
          })}
          <div style={{ width: '1px', height: '20px', background: '#e0e0e0', margin: '0 4px' }} />
          {[
            { key: 'all', label: 'Toutes sources' },
            { key: 'agent', label: 'Agents' },
            { key: 'citizen', label: 'Web direct' }
          ].map(f => {
            const active = source === f.key;
            return (
              <button key={f.key} onClick={() => { setSource(f.key); loadStats(period, f.key, quartierFilter); }}
                style={{ background: active ? '#1B5E20' : 'white', border: '1.5px solid ' + (active ? '#1B5E20' : '#e0e0e0'), color: active ? 'white' : '#555', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}>
                {f.label}
              </button>
            );
          })}
          <div style={{ width: '1px', height: '20px', background: '#e0e0e0', margin: '0 4px' }} />
          {/* Filtre quartier */}
          <select
            value={quartierFilter}
            onChange={e => { setQuartierFilter(e.target.value); loadStats(period, source, e.target.value); }}
            style={{ padding: '6px 12px', borderRadius: '8px', border: '1.5px solid #e0e0e0', fontSize: '12px', fontWeight: '500', color: quartierFilter !== 'all' ? '#1B5E20' : '#555', background: quartierFilter !== 'all' ? '#E8F5E9' : 'white', cursor: 'pointer', outline: 'none' }}
          >
            <option value="all">Tous les quartiers</option>
            {stats?.by_quartier?.map(q => (
              <option key={q.quartier} value={q.quartier}>{q.quartier}</option>
            ))}
          </select>
        </div>

        {/* Score global */}
        {stats?.overview?.total_sessions > 0 && (
          <div style={{ marginBottom: '24px', background: 'linear-gradient(135deg, #1B5E20, #2E7D32)', borderRadius: '16px', padding: '24px', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '600', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '1px' }}>Indice de Satisfaction Citoyenne</div>
              <div style={{ fontSize: '42px', fontWeight: '700', marginTop: '4px' }}>
                {(() => {
                  const cr = stats.overview.completion_rate || 0;
                  const sat = stats.satisfaction || [];
                  const totalSat = sat.reduce((s, x) => s + x.count, 0) || 1;
                  const pos = sat.filter(s => /satisfait|bon|très|excellent/i.test(s.level)).reduce((s, x) => s + x.count, 0);
                  const satScore = Math.round((pos / totalSat) * 100) || 0;
                  const score = Math.round((cr * 0.4) + (satScore * 0.6));
                  return score;
                })()}<span style={{ fontSize: '18px', opacity: 0.7 }}>/100</span>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '13px', opacity: 0.8 }}>Basé sur {stats.overview.total_sessions} participations</div>
              <div style={{ fontSize: '12px', opacity: 0.6, marginTop: '4px' }}>Mise à jour automatique</div>
            </div>
            <Star size={48} color="rgba(255,255,255,0.15)" />
          </div>
        )}

        {/* Stats Cards avec tendances */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
          {(() => {
            const trends = stats?.trends || {};
            const wDiff = trends.last_week > 0 ? Math.round(((trends.this_week - trends.last_week) / trends.last_week) * 100) : 0;
            return [
              { icon: <Users size={20} />, label: 'Participations', value: stats?.overview?.total_sessions || 0, color: '#2E7D32', bg: '#E8F5E9', trend: wDiff > 0 ? `+${wDiff}% vs sem. dernière` : wDiff < 0 ? `${wDiff}% vs sem. dernière` : null, trendUp: wDiff > 0 },
              { icon: <CheckCircle2 size={20} />, label: 'Terminees', value: stats?.overview?.completed_sessions || 0, color: '#4CAF50', bg: '#E8F5E9' },
              { icon: <Percent size={20} />, label: 'Taux completion', value: `${stats?.overview?.completion_rate || 0}%`, color: '#FFA000', bg: '#FFF8E1' },
              { icon: <Calendar size={20} />, label: "Aujourd'hui", value: stats?.overview?.today_sessions || 0, color: '#1B5E20', bg: '#E8F5E9' },
            ].map((card, i) => (
              <div key={i} style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.color }}>{card.icon}</div>
                </div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a' }}>{card.value}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
                  <div style={{ fontSize: '12px', color: '#888' }}>{card.label}</div>
                  {card.trend && <div style={{ fontSize: '11px', color: card.trendUp ? '#2E7D32' : '#E53935', fontWeight: '600' }}>{card.trend}</div>}
                </div>
              </div>
            ));
          })()}
        </div>

        {/* Charts Row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
          {/* Satisfaction Chart */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: '#333' }}>Satisfaction Generale</h3>
            {stats?.satisfaction && stats.satisfaction.length > 0 ? (
              <Doughnut data={{
                labels: stats.satisfaction.map(s => s.level),
                datasets: [{ data: stats.satisfaction.map(s => s.count), backgroundColor: chartColors, borderWidth: 0 }]
              }} options={{ responsive: true, plugins: { legend: { position: 'bottom', labels: { font: { size: 11 } } } } }} />
            ) : <p style={{ color: '#999', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>Aucune donnee</p>}
          </div>

          {/* By Quartier */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: '#333' }}>Participation par Quartier</h3>
            {stats?.by_quartier && stats.by_quartier.length > 0 ? (
              <Bar data={{
                labels: stats.by_quartier.map(q => q.quartier),
                datasets: [{ label: 'Participants', data: stats.by_quartier.map(q => q.count), backgroundColor: '#4CAF50', borderRadius: 6 }]
              }} options={{ responsive: true, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }} />
            ) : <p style={{ color: '#999', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>Aucune donnee</p>}
          </div>
        </div>

        {/* Trend + Demographics */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
          {/* Daily Trend */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: '#333' }}>Evolution des Participations</h3>
            {stats?.daily_trend && stats.daily_trend.length > 0 ? (
              <Line data={{
                labels: stats.daily_trend.map(d => new Date(d.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })),
                datasets: [{ label: 'Participations', data: stats.daily_trend.map(d => d.count), borderColor: '#2E7D32', backgroundColor: 'rgba(46,125,50,0.1)', fill: true, tension: 0.4 }]
              }} options={{ responsive: true, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }} />
            ) : <p style={{ color: '#999', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>Aucune donnee</p>}
          </div>

          {/* Gender + Age */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: '#333' }}>Par Genre</h3>
            {stats?.by_gender && stats.by_gender.length > 0 ? (
              <Doughnut data={{
                labels: stats.by_gender.map(g => g.gender),
                datasets: [{ data: stats.by_gender.map(g => g.count), backgroundColor: ['#2E7D32', '#FFD54F'], borderWidth: 0 }]
              }} options={{ responsive: true, plugins: { legend: { position: 'bottom', labels: { font: { size: 11 } } } } }} />
            ) : <p style={{ color: '#999', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>Aucune donnee</p>}
          </div>
        </div>

        {/* Top Agents */}
        {stats?.top_agents && stats.top_agents.length > 0 && (
          <div style={{ marginTop: '24px', background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <UserPlus size={18} color="#1565C0" /> Classement des agents
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <th style={{ textAlign: 'left', padding: '10px 12px', color: '#888', fontWeight: '600' }}>Agent</th>
                    <th style={{ textAlign: 'right', padding: '10px 12px', color: '#888', fontWeight: '600' }}>Enquêtes</th>
                    <th style={{ textAlign: 'right', padding: '10px 12px', color: '#888', fontWeight: '600' }}>Terminées</th>
                    <th style={{ textAlign: 'right', padding: '10px 12px', color: '#888', fontWeight: '600' }}>Taux</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.top_agents.map((a, i) => (
                    <tr key={a.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: i === 0 ? '#FFD54F' : '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: '#1a1a1a' }}>{i + 1}</div>
                        <span style={{ color: '#333', fontWeight: '600' }}>{a.full_name || a.username}</span>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: '#333', fontWeight: '600' }}>{a.total_sessions}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: '#666' }}>{a.completed_sessions}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', padding: '3px 10px', borderRadius: '10px', background: a.completion_rate >= 80 ? '#E8F5E9' : a.completion_rate >= 50 ? '#FFF8E1' : '#FFEBEE', color: a.completion_rate >= 80 ? '#2E7D32' : a.completion_rate >= 50 ? '#FFA000' : '#E53935' }}>
                          {a.completion_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Satisfaction par domaine */}
        {domains.length > 0 && (
          <div style={{ marginTop: '24px', background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '20px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Shield size={18} color="#2E7D32" /> Satisfaction par domaine d'action municipale
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {domains.map((d, i) => (
                <div key={i} style={{ padding: '10px 12px', background: '#fafafa', borderRadius: '10px', border: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', fontSize: '13px' }}>
                    <span style={{ color: '#333', fontWeight: '500' }}>{d.domain}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ color: d.average >= 60 ? '#2E7D32' : d.average >= 40 ? '#FFA000' : '#E53935', fontWeight: '700' }}>{d.average}%</span>
                      <button onClick={() => setSelectedDomain(d)} style={{ fontSize: '11px', color: '#1565C0', background: '#E3F2FD', border: 'none', borderRadius: '6px', padding: '3px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px', fontWeight: '600' }}>
                        <Eye size={12} /> Voir détails
                      </button>
                    </div>
                  </div>
                  <div style={{ height: '8px', background: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${d.average}%`, background: d.average >= 60 ? '#4CAF50' : d.average >= 40 ? '#FFA000' : '#E53935', borderRadius: '4px', transition: 'width 0.5s' }} />
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '16px', padding: '12px', background: '#f8faf8', borderRadius: '10px', fontSize: '12px', color: '#666' }}>
              Moyenne générale : <span style={{ fontWeight: '700', color: '#1a1a1a' }}>{Math.round(domains.reduce((s, d) => s + d.average, 0) / domains.length)}%</span>
            </div>
          </div>
        )}

        {/* Modal détails domaine */}
        {selectedDomain && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setSelectedDomain(null)}>
            <div style={{ background: 'white', borderRadius: '20px', padding: '28px', maxWidth: '520px', width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>{selectedDomain.domain}</h3>
                <button onClick={() => setSelectedDomain(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999' }}>
                  <X size={20} />
                </button>
              </div>

              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ fontSize: '36px', fontWeight: '700', color: selectedDomain.average >= 60 ? '#2E7D32' : selectedDomain.average >= 40 ? '#FFA000' : '#E53935' }}>{selectedDomain.average}%</div>
                <div style={{ fontSize: '13px', color: '#888' }}>Score moyen sur {selectedDomain.count} réponse{selectedDomain.count > 1 ? 's' : ''}</div>
              </div>

              {/* Répartition détaillée */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Répartition des avis</h4>
                {(() => {
                  const levels = ['Très satisfait(e)', 'Satisfait(e)', 'Peu satisfait(e)', 'Pas du tout satisfait(e)', 'Sans avis'];
                  const colors = { 'Très satisfait(e)': '#2E7D32', 'Satisfait(e)': '#66BB6A', 'Peu satisfait(e)': '#FFA000', 'Pas du tout satisfait(e)': '#E53935', 'Sans avis': '#9E9E9E' };
                  const total = selectedDomain.count || 1;
                  return levels.map(level => {
                    const count = selectedDomain.breakdown?.[level] || 0;
                    const pct = Math.round((count / total) * 100);
                    if (count === 0) return null;
                    return (
                      <div key={level} style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                          <span style={{ color: '#444', fontWeight: '500' }}>{level}</span>
                          <span style={{ color: '#666', fontWeight: '600' }}>{count} ({pct}%)</span>
                        </div>
                        <div style={{ height: '10px', background: '#f0f0f0', borderRadius: '5px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: colors[level] || '#999', borderRadius: '5px', transition: 'width 0.4s' }} />
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Mini graphique doughnut */}
              {(() => {
                const levels = ['Très satisfait(e)', 'Satisfait(e)', 'Peu satisfait(e)', 'Pas du tout satisfait(e)', 'Sans avis'];
                const colors = ['#2E7D32', '#66BB6A', '#FFA000', '#E53935', '#9E9E9E'];
                const data = levels.map(l => selectedDomain.breakdown?.[l] || 0).filter(c => c > 0);
                const labels = levels.filter(l => (selectedDomain.breakdown?.[l] || 0) > 0);
                const usedColors = levels.map((l, i) => (selectedDomain.breakdown?.[l] || 0) > 0 ? colors[i] : null).filter(Boolean);
                if (data.length === 0) return null;
                return (
                  <div style={{ maxWidth: '260px', margin: '0 auto' }}>
                    <Doughnut data={{
                      labels,
                      datasets: [{ data, backgroundColor: usedColors, borderWidth: 0 }]
                    }} options={{ responsive: true, plugins: { legend: { position: 'bottom', labels: { font: { size: 10 }, boxWidth: 10 } } } }} />
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Top priorités citoyens */}
        {priorities.length > 0 && (
          <div style={{ marginTop: '24px', background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '20px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Target size={18} color="#1565C0" /> Priorités pour les 5 prochaines années
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {priorities.slice(0, 5).map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ minWidth: '28px', height: '28px', borderRadius: '50%', background: i === 0 ? '#FFD54F' : i === 1 ? '#e0e0e0' : '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', color: i === 0 ? '#1a1a1a' : '#666' }}>
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                      <span style={{ color: '#333', fontWeight: '500' }}>{p.name}</span>
                      <span style={{ color: '#1565C0', fontWeight: '700' }}>{p.percent}%</span>
                    </div>
                    <div style={{ height: '6px', background: '#f0f0f0', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${p.percent}%`, background: '#1565C0', borderRadius: '3px' }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Leçons & Recommandations */}
        <div style={{ marginTop: '32px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a1a', marginBottom: '20px', fontFamily: 'Playfair Display, serif', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Lightbulb size={20} color="#FFD54F" /> Leçons pour le Maire
          </h2>

          {stats?.overview?.total_sessions > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
              {/* Forces identifiées */}
              <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', borderTop: '4px solid #4CAF50' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#2E7D32', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <ArrowUpRight size={16} /> Forces identifiées
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {(() => {
                    const items = [];
                    const sat = stats.satisfaction || [];
                    if (sat.length > 0) {
                      const top = sat.reduce((a, b) => a.count > b.count ? a : b);
                      if (/satisfait|bon|très|excellent/i.test(top.level)) items.push(`La satisfaction générale est plutôt positive (« ${top.level} »).`);
                    }
                    const quartiers = stats.by_quartier || [];
                    if (quartiers.length > 0) {
                      const topQ = quartiers.reduce((a, b) => a.count > b.count ? a : b);
                      items.push(`Les habitants de ${topQ.quartier} participent davantage (${topQ.count} participants).`);
                    }
                    if (domains.length > 0) {
                      const topD = [...domains].sort((a, b) => b.average - a.average)[0];
                      if (topD.average >= 70) items.push(`Le domaine « ${topD.domain} » est le mieux noté (${topD.average}%).`);
                    }
                    if (items.length === 0) items.push('Continuez la collecte pour identifier les forces.');
                    return items.map((t, i) => (
                      <div key={i} style={{ padding: '10px 12px', background: '#E8F5E9', borderRadius: '8px', fontSize: '13px', color: '#2E7D32', lineHeight: '1.5' }}>{t}</div>
                    ));
                  })()}
                </div>
              </div>

              {/* Points de vigilance */}
              <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', borderTop: '4px solid #FFA000' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#E65100', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <AlertTriangle size={16} /> Points de vigilance
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {(() => {
                    const items = [];
                    const cr = stats.overview.completion_rate || 0;
                    if (cr < 50) items.push(`Le taux de complétion (${cr}%) est faible.`);
                    else if (cr < 80) items.push(`Le taux de complétion (${cr}%) pourrait être amélioré.`);
                    const quartiers = stats.by_quartier || [];
                    if (quartiers.length > 0) {
                      const weakQ = [...quartiers].sort((a, b) => a.count - b.count)[0];
                      if (weakQ.count < 3) items.push(`Certains quartiers sont sous-représentés (ex: ${weakQ.quartier}).`);
                    }
                    const gender = stats.by_gender || [];
                    if (gender.length === 2) {
                      const masc = gender.find(g => /masculin/i.test(g.gender));
                      const fem = gender.find(g => /féminin/i.test(g.gender));
                      if (masc && fem && masc.count > fem.count * 2) items.push('Les femmes participent moins que les hommes.');
                    }
                    if (domains.length > 0) {
                      const weakD = [...domains].sort((a, b) => a.average - b.average)[0];
                      if (weakD.average < 50) items.push(`Le domaine « ${weakD.domain} » est le moins bien noté (${weakD.average}%).`);
                    }
                    if (items.length === 0) items.push('Aucun point de vigilance majeur à ce stade.');
                    return items.map((t, i) => (
                      <div key={i} style={{ padding: '10px 12px', background: '#FFF3E0', borderRadius: '8px', fontSize: '13px', color: '#E65100', lineHeight: '1.5' }}>{t}</div>
                    ));
                  })()}
                </div>
              </div>

              {/* Actions recommandées */}
              <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', borderTop: '4px solid #1565C0' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1565C0', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Award size={16} /> Actions recommandées
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {(() => {
                    const items = [];
                    const cr = stats.overview.completion_rate || 0;
                    if (cr < 50) items.push('Réduire la longueur du questionnaire ou simplifier les questions.');
                    const quartiers = stats.by_quartier || [];
                    if (quartiers.length > 0) {
                      const weakQ = [...quartiers].sort((a, b) => a.count - b.count)[0];
                      if (weakQ.count < 3) items.push(`Déployer des agents dans le quartier ${weakQ.quartier} (sous-représenté).`);
                    }
                    const today = stats.overview.today_sessions || 0;
                    if (today === 0) items.push('Renforcer la communication auprès des citoyens (WhatsApp, réunions de quartier).');
                    const gender = stats.by_gender || [];
                    if (gender.length === 2) {
                      const masc = gender.find(g => /masculin/i.test(g.gender));
                      const fem = gender.find(g => /féminin/i.test(g.gender));
                      if (masc && fem && masc.count > fem.count * 2) items.push('Renforcer la communication auprès des femmes et des jeunes.');
                    }
                    if (domains.length > 0) {
                      const weakD = [...domains].sort((a, b) => a.average - b.average)[0];
                      if (weakD.average < 50) items.push(`Investir dans le domaine « ${weakD.domain} » (moins bien noté).`);
                    }
                    if (items.length === 0) items.push('Maintenir la cadence et surveiller les indicateurs.');
                    return items.map((t, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', background: '#E3F2FD', borderRadius: '8px', fontSize: '13px', color: '#1565C0', lineHeight: '1.5' }}>
                        <div style={{ minWidth: '20px', height: '20px', borderRadius: '50%', background: '#1565C0', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', marginTop: '2px' }}>{i + 1}</div>
                        {t}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ background: 'white', borderRadius: '14px', padding: '32px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', color: '#888' }}>
              <p style={{ fontSize: '14px' }}>Commencez la collecte pour générer des leçons.</p>
            </div>
          )}
        </div>

        {/* Commentaires citoyens */}
        {comments.length > 0 && (
          <div style={{ marginTop: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a1a', fontFamily: 'Playfair Display, serif', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <MessageCircle size={20} color="#2E7D32" /> Ce que disent les citoyens
              </h2>
              <div style={{ position: 'relative' }}>
                <Search size={14} color="#999" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="text"
                  placeholder="Rechercher un mot-clé..."
                  value={commentSearch}
                  onChange={e => setCommentSearch(e.target.value)}
                  style={{ padding: '8px 10px 8px 30px', borderRadius: '8px', border: '1px solid #e0e0e0', fontSize: '13px', width: '220px', outline: 'none' }}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
              {comments.filter(c => !commentSearch || c.text.toLowerCase().includes(commentSearch.toLowerCase())).slice(0, 6).map((c, i) => (
                <div key={i} style={{ background: 'white', borderRadius: '14px', padding: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', borderLeft: '3px solid #4CAF50' }}>
                  <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px', fontStyle: 'italic' }}>{c.question}</div>
                  <div style={{ fontSize: '14px', color: '#333', lineHeight: '1.5', fontWeight: '500' }}>« {c.text} »</div>
                </div>
              ))}
              {commentSearch && comments.filter(c => c.text.toLowerCase().includes(commentSearch.toLowerCase())).length === 0 && (
                <div style={{ color: '#888', fontSize: '13px', padding: '20px' }}>Aucun commentaire ne correspond.</div>
              )}
            </div>
          </div>
        )}

        {/* Prévision */}
        {stats?.overview?.total_sessions > 0 && stats?.daily_trend && stats.daily_trend.length >= 3 && (
          <div style={{ marginTop: '24px', background: 'white', borderRadius: '14px', padding: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#E3F2FD', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1565C0' }}>
              <Zap size={20} />
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#333' }}>Prévision</div>
              {(() => {
                const total = stats.overview.total_sessions;
                const days = stats.daily_trend.length;
                const avgPerDay = total / days;
                const projected30 = Math.round(avgPerDay * 30);
                const remaining = Math.max(0, 500 - total);
                const daysTo500 = avgPerDay > 0 ? Math.ceil(remaining / avgPerDay) : 0;
                return (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Au rythme actuel ({Math.round(avgPerDay)} /jour), environ <strong style={{ color: '#1565C0' }}>{projected30}</strong> participations sont attendues dans les 30 prochains jours.
                    {daysTo500 > 0 && daysTo500 < 365 && ` Objectif 500 : ~${daysTo500} jours restants.`}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Données brutes pour analyse du Maire */}
        <div style={{ marginTop: '32px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a1a', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>
            Données brutes
          </h2>

          {stats?.overview?.total_sessions > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
              {/* Tableau satisfaction */}
              {stats?.satisfaction && stats.satisfaction.length > 0 && (
                <div style={{ background: 'white', borderRadius: '14px', padding: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '14px', color: '#333' }}>Satisfaction générale — répartition</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <th style={{ textAlign: 'left', padding: '8px 0', color: '#888', fontWeight: '500' }}>Niveau</th>
                        <th style={{ textAlign: 'right', padding: '8px 0', color: '#888', fontWeight: '500' }}>Nombre</th>
                        <th style={{ textAlign: 'right', padding: '8px 0', color: '#888', fontWeight: '500' }}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const totalSat = stats.satisfaction.reduce((s, x) => s + x.count, 0);
                        return stats.satisfaction.map((s, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                            <td style={{ padding: '10px 0', color: '#333' }}>{s.level}</td>
                            <td style={{ padding: '10px 0', textAlign: 'right', color: '#333', fontWeight: '600' }}>{s.count}</td>
                            <td style={{ padding: '10px 0', textAlign: 'right', color: '#666' }}>{Math.round(s.count / totalSat * 100)}%</td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tableau quartiers */}
              {stats?.by_quartier && stats.by_quartier.length > 0 && (
                <div style={{ background: 'white', borderRadius: '14px', padding: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '14px', color: '#333' }}>Participation par quartier</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <th style={{ textAlign: 'left', padding: '8px 0', color: '#888', fontWeight: '500' }}>Quartier</th>
                        <th style={{ textAlign: 'right', padding: '8px 0', color: '#888', fontWeight: '500' }}>Participants</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.by_quartier.map((q, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          <td style={{ padding: '10px 0', color: '#333' }}>{q.quartier}</td>
                          <td style={{ padding: '10px 0', textAlign: 'right', color: '#333', fontWeight: '600' }}>{q.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tableau source */}
              {stats?.by_source && stats.by_source.length > 0 && (
                <div style={{ background: 'white', borderRadius: '14px', padding: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '14px', color: '#333' }}>Source des enquêtes</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <th style={{ textAlign: 'left', padding: '8px 0', color: '#888', fontWeight: '500' }}>Source</th>
                        <th style={{ textAlign: 'right', padding: '8px 0', color: '#888', fontWeight: '500' }}>Total</th>
                        <th style={{ textAlign: 'right', padding: '8px 0', color: '#888', fontWeight: '500' }}>Terminées</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.by_source.map((s, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          <td style={{ padding: '10px 0', color: '#333' }}>{s.source_type === 'agent' ? 'Agents' : 'Citoyens directs'}</td>
                          <td style={{ padding: '10px 0', textAlign: 'right', color: '#333', fontWeight: '600' }}>{s.count}</td>
                          <td style={{ padding: '10px 0', textAlign: 'right', color: '#666' }}>{s.completed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tableau genre */}
              {stats?.by_gender && stats.by_gender.length > 0 && (
                <div style={{ background: 'white', borderRadius: '14px', padding: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '14px', color: '#333' }}>Répartition par genre</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <th style={{ textAlign: 'left', padding: '8px 0', color: '#888', fontWeight: '500' }}>Genre</th>
                        <th style={{ textAlign: 'right', padding: '8px 0', color: '#888', fontWeight: '500' }}>Nombre</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.by_gender.map((g, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          <td style={{ padding: '10px 0', color: '#333' }}>{g.gender}</td>
                          <td style={{ padding: '10px 0', textAlign: 'right', color: '#333', fontWeight: '600' }}>{g.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tableau age */}
              {stats?.by_age && stats.by_age.length > 0 && (
                <div style={{ background: 'white', borderRadius: '14px', padding: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '14px', color: '#333' }}>Répartition par âge</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #eee' }}>
                        <th style={{ textAlign: 'left', padding: '8px 0', color: '#888', fontWeight: '500' }}>Tranche</th>
                        <th style={{ textAlign: 'right', padding: '8px 0', color: '#888', fontWeight: '500' }}>Nombre</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.by_age.map((a, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          <td style={{ padding: '10px 0', color: '#333' }}>{a.age_group}</td>
                          <td style={{ padding: '10px 0', textAlign: 'right', color: '#333', fontWeight: '600' }}>{a.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div style={{ background: 'white', borderRadius: '14px', padding: '32px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', color: '#888' }}>
              <p style={{ fontSize: '14px' }}>Aucune donnée collectée pour le moment.</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default AdminDashboard;
